import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { createAdminWorkspace } from '@/services/authApi';
import { apiErrorText } from './apiErrorText';

const ADMIN_WORKSPACES_KEY = ['admin', 'workspaces'] as const;

/**
 * Admin > Workspaces > "New workspace": a workspace with a name and a website and NO owner and NO members.
 * A full client (contract, brand, Google Ads, members) goes through New client.
 */
export function NewWorkspaceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [pkg, setPkg] = useState<'seo' | 'ads' | 'both'>('both');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createAdminWorkspace({ name: name.trim(), website: website.trim(), package: pkg }),
    onSuccess: () => {
      toast.success('Workspace created');
      queryClient.invalidateQueries({ queryKey: ADMIN_WORKSPACES_KEY });
      reset();
      onClose();
    },
    onError: (err: unknown) => setError(apiErrorText(err, 'Could not create the workspace')),
  });

  function reset() {
    setName(''); setWebsite(''); setPkg('both'); setError(null);
  }

  const blank = !name.trim() || !website.trim();

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) { reset(); onClose(); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New workspace</DialogTitle>
          <DialogDescription>
            Creates an empty workspace with no owner. A client that also needs a contract, brand and
            members is created with New client:{' '}
            <Link to="/clients" className="text-link underline">Use New client for a full client</Link>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="new-ws-name">Workspace name</Label>
            <Input id="new-ws-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-ws-website">Website</Label>
            <Input id="new-ws-website" placeholder="example.com" value={website} onChange={(e) => setWebsite(e.target.value)} />
            <p className="text-xs text-muted-foreground">The website is the client&apos;s ID and cannot be changed later.</p>
          </div>
          <div className="space-y-1">
            <Label>Package</Label>
            <Select value={pkg} onValueChange={(v) => setPkg(v as 'seo' | 'ads' | 'both')}>
              <SelectTrigger aria-label="Package for new workspace" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="seo">SEO</SelectItem>
                <SelectItem value="ads">Ads</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          <Button onClick={() => { setError(null); create.mutate(); }} disabled={blank || create.isPending}>
            Create workspace
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NewWorkspaceDialog;
